/* HØLY · holy-reels — owned reels carousel coordinator.
   Forked from Wonder video-reels.js (2026-06-26): custom element renamed
   <video-reels> -> <holy-reels> so it cannot collide with Wonder's element.
   Internal logic preserved 1:1. Drives the active-slide play/mute/sound behavior
   on top of the shared Swiper slider (assets/slider.js, read-only infra). */
if (!customElements.get("holy-reels")) {
  customElements.define(
    "holy-reels",
    class HolyReels extends HTMLElement {
      constructor() {
        super();
        this.activeClass = "active";
        this.swiper = this.querySelector(".wt-slider__container");
      }

      connectedCallback() {
        this._sectionObserver = null;
        this.init();
      }

      disconnectedCallback() {
        if (this._sectionObserver) {
          this._sectionObserver.disconnect();
          this._sectionObserver = null;
        }
        if (this.checkInterval) clearInterval(this.checkInterval);
      }

      observeSection() {
        const sectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = this.querySelector(".swiper-slide-active video");
              if (video) {
                if (entry.isIntersecting) video.play().catch(() => {});
                else video.pause();
              }
            });
          },
          { root: null, rootMargin: "0px", threshold: 0.1 },
        );
        sectionObserver.observe(this);
        this._sectionObserver = sectionObserver;
      }

      hardStopAllExceptActive(swiper) {
        const active = swiper.slides[swiper.activeIndex];
        swiper.slides.forEach((slide) => {
          const video = slide.querySelector("video");
          if (!video) return;
          if (slide !== active) {
            video.pause();
            video.muted = true;
            video.removeAttribute("autoplay");
            video.autoplay = false;
          }
        });
      }

      handleSoundToggle(swiper) {
        swiper.slides.forEach((slide) => {
          const button = slide.querySelector(".wt-video__sound-toggle");
          if (!button || button._bound) return;
          button._bound = true;
          button.addEventListener(
            "click",
            () => {
              const activeSlide = swiper.slides[swiper.activeIndex];
              const activeVideo = activeSlide?.querySelector("video");
              if (!activeVideo) return;
              this.hardStopAllExceptActive(swiper);
              const soundOn = swiper.el.dataset.sound === "on";
              const nextSoundOn = !soundOn;
              swiper.el.dataset.sound = nextSoundOn ? "on" : "off";
              activeVideo.muted = !nextSoundOn;
              activeVideo.setAttribute("playsinline", "");
              activeVideo.setAttribute("webkit-playsinline", "");
              activeVideo.removeAttribute("autoplay");
              activeVideo.autoplay = false;
              setTimeout(() => { activeVideo.play().catch(() => {}); }, 80);
            },
            { passive: true },
          );
        });
      }

      sanitizeVideosOnce() {
        if (this._sanitized) return;
        this._sanitized = true;
        this.querySelectorAll("video").forEach((video) => {
          video.pause();
          video.muted = true;
          video.removeAttribute("autoplay");
          video.autoplay = false;
          video.setAttribute("playsinline", "");
          video.setAttribute("webkit-playsinline", "");
          video.preload = "none";
        });
      }

      playVideoInActiveSlide(swiper) {
        const sound = swiper.el.dataset.sound;
        const activeSlideVideo = this.findActiveSlide(swiper)?.querySelector("video");
        if (activeSlideVideo) {
          activeSlideVideo.muted = sound !== "on";
          setTimeout(() => {
            activeSlideVideo.play().catch(() => {});
          }, 100);
        }
      }

      findActiveSlide(swiper) {
        return swiper.slides[swiper.activeIndex];
      }

      toggleActiveClass(swiper) {
        const activeSlide = this.findActiveSlide(swiper);
        swiper.slides.forEach((slide) => {
          slide.classList.remove(this.activeClass);
          if (activeSlide === slide) slide.classList.add(this.activeClass);
        });
      }

      handleSlideChange(swiper) {
        this.hardStopAllExceptActive(swiper);
        this.toggleActiveClass(swiper);
        this.playVideoInActiveSlide(swiper);
      }

      addVideoEventHandlers(swiper) {
        this._onSlideChange = () => this.handleSlideChange(swiper);
        swiper.on("slideChange", this._onSlideChange);
        swiper.on("slidesLengthChange", () => this.handleSoundToggle(swiper));
        swiper.on("update", () => this.handleSoundToggle(swiper));
      }

      checkSwiperInitialization() {
        const swiperContainer = this.swiper;
        const instance = swiperContainer?.swiper;
        if (swiperContainer?.classList.contains("swiper-initialized") && instance) {
          if (!this.swiper?.dataset.sound) this.swiper.dataset.sound = "off";
          this.sanitizeVideosOnce();
          this.addVideoEventHandlers(instance);
          this.handleSlideChange(instance);
          this.handleSoundToggle(instance);
          this.observeSection();
          clearInterval(this.checkInterval);
        }
      }

      init() {
        this.checkInterval = setInterval(this.checkSwiperInitialization.bind(this), 250);
      }
    },
  );
}
